import DOMPurify from '~/../custom-modules/dompurify/dompurify.js';
import { useFocusWithin } from '~/logic/composables/useFocusWithin.js';
import { installDirectiveAA } from '~/logic/adobe-analytic.js';
import { twMerge } from 'tailwind-merge';


export default {
	install (app, options) {
		app.directive('focus-within', {
			mounted (el, binding, vnode, prevVnode) {
				const { focused, activeElement } = useFocusWithin(el);
				watch(focused, (newValue) => {
					if (newValue) {
						// is focused within
						el.dispatchEvent(new CustomEvent('focus-within', { detail: { activeElement } }));
					} else {
						// is not focused within
						el.dispatchEvent(new CustomEvent('blur-within', { detail: { activeElement } }));
					}
				});
			},
		});
		
		app.directive('button-press', {
			mounted (el, binding, vnode, prevVnode) {
				const CLASSNAME = 'is-mouse-down';
				const isMouseDown = ref(false);
				
				const setIsMouseDownTrue = () => { isMouseDown.value = true; };
				const setIsMouseDownFalse = () => { isMouseDown.value = false; };
				
				el.addEventListener('mousedown', setIsMouseDownTrue);
				el.addEventListener('mouseup', setIsMouseDownFalse);

				el.addEventListener('keydown', (event) => {
					if (['Enter', 'Space'].includes(event.code)) {
						setIsMouseDownTrue();
					}
				});
				el.addEventListener('keyup', (event) => {
					if (['Enter', 'Space'].includes(event.code)) {
						setIsMouseDownFalse();
					}
				});
				
				const unwatch = watch(isMouseDown, (newValue) => {
					if (newValue) {
						el.classList.add(CLASSNAME);
					} else {
						el.classList.remove(CLASSNAME);
					}
				});
				
				// for clean up
				el.__button_setIsMouseDownTrue = setIsMouseDownTrue;
				el.__button_setIsMouseDownFalse = setIsMouseDownFalse;
				el.__button_unwatch = unwatch;
			},
			
			unmounted (el) {
				el.removeEventListener('mousedown', el.__button_setIsMouseDownTrue);
				el.removeEventListener('mouseup', el.__button_setIsMouseDownFalse);
				el.removeEventListener('keydown', el.__button_setIsMouseDownTrue);
				el.removeEventListener('keyup', el.__button_setIsMouseDownFalse);
				el.__button_unwatch();
			},
		});
		
		app.directive('visible', (el, binding, vnode, prevVnode) => {
			const value = binding.value;
			
			if (value) {
				el.style.visibility = 'visible';
			} else {
				el.style.visibility = 'hidden';
			}
		});
		
		app.directive('html-sanitize', (el, binding, vnode, prevVnode) => {
			const FORBID_TAGS = binding.modifiers.allowStyle ? [] : ['style'];
			
			if (binding.value !== binding.oldValue) {
				el.innerHTML = DOMPurify.sanitize(binding.value, {
					FORBID_TAGS,
				});
			}
		});
		
		app.directive('alphabets-strict', {
			mounted (el, binding, vnode, prevVnode) {
				const restrictToAlphabets = (e) => {
					if (!/[a-zA-Z\s\b]/.test(e.key)) {
						e.preventDefault();
					}
				};
				el.addEventListener('keydown', restrictToAlphabets);
				el.__restrictToAlphabets = restrictToAlphabets;
			},
			
			unmounted (el) {
				el.removeEventListener('keydown', el.__restrictToAlphabets);
			},
		});

		app.directive('roman-only', {
			mounted (el) {
				const input = el.tagName === 'INPUT' ? el : el.querySelector('input');
				if (!input) return;
				let isComposing = false;
				const invalidCharsRegex = /[^A-Za-z\s'-]/g;
				const sanitize = () => {
					const cleaned = input.value.replace(invalidCharsRegex, '');
					if (cleaned !== input.value) {
						input.value = cleaned;
						input.dispatchEvent(
							new Event('input', { bubbles: true })
						);
					}
				};
				const handleInput = () => {
					if (!isComposing) {
						sanitize();
					}
				};
				const handleCompositionStart = () => {
					isComposing = true;
				};
				const handleCompositionEnd = () => {
					isComposing = false;
					sanitize();
				};
				input.addEventListener('input', handleInput);
				input.addEventListener('compositionstart', handleCompositionStart);
				input.addEventListener('compositionend', handleCompositionEnd);
				el.__romanOnly = {
					input,
					handleInput,
					handleCompositionStart,
					handleCompositionEnd,
				};
			},
			unmounted (el) {
				const handlers = el.__romanOnly;
				if (!handlers) return;
				handlers.input.removeEventListener('input', handlers.handleInput);
				handlers.input.removeEventListener(
					'compositionstart',
					handlers.handleCompositionStart
				);
				handlers.input.removeEventListener(
					'compositionend',
					handlers.handleCompositionEnd
				);
			},
		});
		
		// ---------------- Tailwind Merge Directive ----------------
		// https://github.com/yakir-reznik/tailwind-merge-vue-directive
		const twMergeComputeClasses = (el, binding, vNode) => {
			const existingClasses = el.classList.value;
			const inheritedClasses = vNode?.ctx?.attrs;

			// No need to run twMerge if there are no classes
			if (!existingClasses || !inheritedClasses) return;

			// This works because all fallthrough classes are added at the end of the string
			el.classList.value = twMerge(existingClasses, inheritedClasses);
		};
		app.directive('tw-merge', {
			beforeMount: twMergeComputeClasses,
			updated: twMergeComputeClasses,
		});
		// -------------- END Tailwind Merge Directive --------------
		
		
		installDirectiveAA(app);
	},
};
